import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Home,
  Calendar,
  ShoppingCart,
  UserPlus,
  GraduationCap,
  Archive,
  Monitor,
  Route,
  Layers,
  Play,
  Box,
  Library,
  FileText,
  ClipboardList,
  BarChart3,
  Receipt,
  TrendingUp,
  FileSpreadsheet,
  CheckSquare,
  Sparkles,
  Store,
  Building2,
  Users,
  UserCog,
  Wallet,
  MapPin,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Settings,
  Shield,
  Mail,
  Moon,
  Sun,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';

const getMenuStructure = (t) => [
  {
    id: 'accueil',
    label: t('nav_home'),
    icon: Home,
    children: [
      { id: 'dashboard', label: t('nav_dashboard'), href: '/admin', icon: Home },
      { id: 'agenda', label: t('nav_agenda'), href: '/admin/agenda', icon: Calendar },
    ]
  },
  {
    id: 'commercial',
    label: t('nav_commercial'),
    icon: ShoppingCart,
    children: [
      { id: 'tunnel', label: t('nav_salesFunnel'), href: '/commercial/tunnel', icon: TrendingUp },
      { id: 'inscriptions', label: t('nav_registrations'), href: '/commercial/inscriptions', icon: UserPlus },
    ]
  },
  {
    id: 'sessions',
    label: t('nav_trainingSessions'),
    icon: GraduationCap,
    children: [
      { id: 'all-sessions', label: t('nav_allSessions'), href: '/sessions', icon: GraduationCap },
      { id: 'archived-sessions', label: t('nav_archivedSessions'), href: '/sessions/archived', icon: Archive },
    ]
  },
  {
    id: 'elearning',
    label: t('nav_elearning'),
    icon: Monitor,
    children: [
      { id: 'elearning-dashboard', label: t('nav_elearningDashboard'), href: '/elearning', icon: Home },
      { id: 'parcours', label: t('nav_courses'), href: '/elearning/parcours', icon: Route },
      { id: 'sequences', label: t('nav_sequencesAdmin'), href: '/elearning/sequences', icon: Layers },
      { id: 'activities', label: t('nav_activities'), href: '/elearning/activities', icon: Play },
      { id: 'ai-quiz', label: t('nav_aiQuiz'), href: '/elearning/ai-quiz', icon: Sparkles },
      { id: 'scorm', label: t('nav_scorm'), href: '/elearning/scorm', icon: Box },
    ]
  },
  {
    id: 'bibliotheque',
    label: t('nav_library'),
    icon: Library,
    children: [
      { id: 'programmes', label: t('nav_programs'), href: '/bibliotheque/programmes', icon: FileText },
      { id: 'evaluations', label: t('nav_evalsAdmin'), href: '/bibliotheque/evaluations', icon: ClipboardList },
      { id: 'programmes-archives', label: t('nav_archives'), href: '/bibliotheque/archives', icon: Archive },
    ]
  },
  {
    id: 'rapports',
    label: t('nav_reports'),
    icon: BarChart3,
    children: [
      { id: 'suivi-activite', label: t('nav_activityReport'), href: '/rapports/activite', icon: BarChart3 },
      { id: 'suivi-factures', label: t('nav_invoices'), href: '/rapports/factures', icon: Receipt },
      { id: 'suivi-commercial', label: t('nav_commercialReport'), href: '/rapports/commercial', icon: TrendingUp },
      { id: 'bpf', label: t('nav_bpf'), href: '/rapports/bpf', icon: FileSpreadsheet },
      { id: 'suivi-qualite', label: t('nav_quality'), href: '/rapports/qualite', icon: CheckSquare },
      { id: 'amelioration', label: t('nav_improvement'), href: '/rapports/amelioration', icon: Sparkles },
      { id: 'marketplace', label: t('nav_marketplace'), href: '/rapports/marketplace', icon: Store },
    ]
  },
  {
    id: 'donnees',
    label: t('nav_data'),
    icon: Building2,
    children: [
      { id: 'donnees-dashboard', label: t('nav_dashboard'), href: '/donnees', icon: Home },
      { id: 'entreprises', label: t('nav_companies'), href: '/donnees/entreprises', icon: Building2 },
      { id: 'apprenants', label: t('nav_students'), href: '/donnees/apprenants', icon: Users },
      { id: 'intervenants', label: t('nav_trainers'), href: '/donnees/intervenants', icon: UserCog },
      { id: 'financeurs', label: t('nav_funders'), href: '/donnees/financeurs', icon: Wallet },
      { id: 'lieux', label: t('nav_locations'), href: '/donnees/lieux', icon: MapPin },
    ]
  },
  {
    id: 'configuration',
    label: 'Configuration',
    icon: Settings,
    children: [
      { id: 'comptes-acces', label: "Comptes d'accès", href: '/configuration/comptes-acces', icon: Shield },
      { id: 'modeles-documents', label: 'Modèles de documents', href: '/configuration/modeles-documents', icon: FileText },
      { id: 'modeles-emails', label: "Modèles d'emails", href: '/configuration/modeles-emails', icon: Mail },
      { id: 'audit-log', label: "Journal d'audit", href: '/configuration/audit-log', icon: ClipboardList },
    ]
  },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { t, lang, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['accueil']);

  const menuStructure = getMenuStructure(t);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isActiveRoute = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isMenuActive = (menu) => {
    return menu.children?.some(child => isActiveRoute(child.href));
  };

  const MenuItem = ({ item }) => {
    const isExpanded = expandedMenus.includes(item.id);
    const isActive = isMenuActive(item);
    const Icon = item.icon;

    return (
      <div className="mb-1">
        <button
          onClick={() => toggleMenu(item.id)}
          className={cn(
            "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
            isActive || isExpanded
              ? "bg-teal-500/10 text-teal-600"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          )}
          data-testid={`menu-${item.id}`}
        >
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5" />
            <span>{item.label}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        
        {isExpanded && item.children && (
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200 pl-4">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const isChildActive = isActiveRoute(child.href);
              return (
                <Link
                  key={child.id}
                  to={child.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                    isChildActive
                      ? "bg-teal-500 text-white font-medium"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )}
                  data-testid={`submenu-${child.id}`}
                >
                  <ChildIcon className="h-4 w-4" />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-3">
          <img src="/affea-logo.png" alt="AFFEA" className="w-10 h-10 rounded-lg object-cover" />
          <div>
            <h1 className="font-display text-xl font-bold text-slate-900">AFFEA</h1>
            <p className="text-xs text-teal-600">Gestion de formation</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {menuStructure.map((item) => (
            <MenuItem key={item.id} item={item} />
          ))}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="p-4 border-t border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-teal-500 text-white font-medium">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-slate-600"
            onClick={() => navigate('/settings')}
            data-testid="settings-btn"
          >
            <Settings className="h-4 w-4 mr-1" />
            {t('nav_settings')}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="text-slate-600 shrink-0"
            onClick={toggleLanguage}
            data-testid="admin-lang-toggle"
            title={lang === 'fr' ? 'Switch to English' : 'Passer en français'}
          >
            <Globe className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="text-slate-600 shrink-0"
            onClick={toggleDarkMode}
            data-testid="dark-mode-toggle"
          >
            {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-72 bg-white border-r border-slate-200 hidden lg:flex flex-col z-50 shadow-sm">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 lg:hidden flex items-center justify-between px-4 z-50 shadow-sm">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/affea-logo.png" alt="AFFEA" className="w-9 h-9 rounded-lg object-cover" />
          <span className="font-display text-lg font-bold text-slate-900">AFFEA</span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="absolute left-0 top-16 bottom-0 w-72 bg-white flex flex-col animate-slide-in shadow-xl">
            <SidebarContent />
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
